import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Square, X } from "lucide-react";
import { api } from "@/lib/api";
import CelebrationModal from "@/components/CelebrationModal";
import Mascot from "@/components/Mascot";

const LS_KEY = "marta.drum.active";
const DEFAULT_MIN_SECONDS = 20 * 60;

function fmt(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function DrumPractice({ onCompleted }) {
  const nav = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [minSeconds, setMinSeconds] = useState(DEFAULT_MIN_SECONDS);
  const [elapsed, setElapsed] = useState(0);
  // phase: 'ready' | 'running' | 'paused' | 'done'
  const [phase, setPhase] = useState("ready");
  const [reflectPrompt, setReflectPrompt] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [starting, setStarting] = useState(false);
  const tickRef = useRef(null);
  const runStartRef = useRef(null);

  // Restore saved session (always resumed as paused — user must tap Resume)
  useEffect(() => {
    (async () => {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) {
        try {
          const d = JSON.parse(saved);
          setSessionId(d.sessionId);
          setMinSeconds(d.minSeconds || DEFAULT_MIN_SECONDS);
          setElapsed(Math.min(d.elapsed || 0, (d.minSeconds || DEFAULT_MIN_SECONDS)));
          setPhase("paused");
          return;
        } catch { /* ignore malformed cache */ }
      }
      // Preview minSeconds for this week without creating a session yet
      try {
        const r = await api.get("/state");
        setMinSeconds((r.data.current_week?.minimum_drum_minutes || 20) * 60);
      } catch { /* keep default */ }
    })();
  }, []);

  // persist (only after a session has started)
  useEffect(() => {
    if (!sessionId) return;
    if (phase === "ready" || phase === "done") return;
    localStorage.setItem(LS_KEY, JSON.stringify({ sessionId, elapsed, minSeconds }));
  }, [sessionId, elapsed, phase, minSeconds]);

  // timer tick
  useEffect(() => {
    if (phase !== "running") { clearInterval(tickRef.current); return; }
    runStartRef.current = Date.now();
    const startElapsed = elapsed;
    tickRef.current = setInterval(() => {
      const e = startElapsed + Math.floor((Date.now() - runStartRef.current) / 1000);
      setElapsed(e);
    }, 500);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // auto-complete at min
  useEffect(() => {
    if (phase === "running" && elapsed >= minSeconds) {
      setPhase("done");
      setReflectPrompt(true);
    }
  }, [elapsed, minSeconds, phase]);

  async function start() {
    if (starting) return;
    setStarting(true);
    try {
      const r = await api.post("/drum-sessions/start");
      setSessionId(r.data.id);
      setMinSeconds(r.data.min_seconds);
      setElapsed(0);
      setPhase("running");
    } finally {
      setStarting(false);
    }
  }

  async function finish(reflection) {
    if (!sessionId) {
      setReflectPrompt(false);
      nav("/");
      return;
    }
    const r = await api.post(`/drum-sessions/${sessionId}/complete`, {
      duration_seconds: elapsed, reflection,
    });
    localStorage.removeItem(LS_KEY);
    const xpItem = (r.data.new_items || []).find((x) => x.kind === "xp" && x.label === "Practice complete");
    if (r.data.qualified && xpItem) {
      setCelebration({ title: "Practice complete!", subtitle: `+${xpItem.amount} XP`, mascot: "celebrating" });
    } else {
      setCelebration({ title: "Great job!", subtitle: "Practice saved", mascot: "happy" });
    }
    setReflectPrompt(false);
    onCompleted?.(r.data.new_items || []);
  }

  function exit() {
    // If a session was created but not completed, leave it saved in LS so they can resume.
    nav("/");
  }

  const remaining = Math.max(0, minSeconds - elapsed);
  const pct = Math.min(100, (elapsed / minSeconds) * 100);
  const requiredMin = Math.round(minSeconds / 60);

  return (
    <div className="min-h-dvh pb-20">
      <div className="flex items-center justify-between pt-5 max-w-md mx-auto lg:max-w-2xl">
        <div>
          <p className="micro-label">Drum</p>
          <h1 className="text-[26px] text-[hsl(var(--ink))] leading-tight">Practice</h1>
        </div>
        <button
          onClick={exit}
          className="btn btn-ghost !py-2 !px-4 text-sm"
          data-testid="drum-exit-btn"
        >
          <X className="w-4 h-4"/> Exit
        </button>
      </div>

      <div className="mt-6 flex flex-col items-center">
        {/* Circular timer */}
        <div className="relative w-72 h-72 lg:w-80 lg:h-80 rounded-full bg-white flex items-center justify-center shadow-[0_30px_60px_-30px_hsla(258,45%,48%,0.5),inset_0_1px_0_rgba(255,255,255,0.9)]">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="46" fill="none" stroke="hsl(var(--surface-2))" strokeWidth="5"/>
            <circle
              cx="50" cy="50" r="46" fill="none"
              stroke="hsl(var(--violet))" strokeWidth="5"
              strokeDasharray={`${pct * 2.89} 999`} strokeLinecap="round"
            />
          </svg>
          <div className="relative text-center">
            <div className="w-20 h-20 mx-auto mb-1">
              <Mascot
                state={phase === "running" ? "playing_drums" : phase === "done" ? "celebrating" : "idle"}
                size={80}
                testId="drum-mascot"
              />
            </div>
            <div className="text-[44px] font-semibold text-[hsl(var(--ink))] tabular-nums leading-none" data-testid="timer-display">
              {phase === "ready" ? fmt(minSeconds) : fmt(remaining)}
            </div>
            <div className="micro-label mt-1" data-testid="timer-phase">
              {phase === "ready" ? "Ready" : phase === "running" ? "Keep going" : phase === "paused" ? "Paused" : "Done!"}
            </div>
          </div>
        </div>

        {/* Controls — different per phase */}
        <div className="mt-8 w-full max-w-xs flex flex-col gap-3">
          {phase === "ready" && (
            <button
              onClick={start}
              disabled={starting}
              className="btn btn-primary w-full !py-4 text-lg disabled:opacity-70"
              data-testid="start-practice-btn"
            >
              <Play className="w-5 h-5"/> {starting ? "Starting…" : "Start practice"}
            </button>
          )}
          {phase === "running" && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPhase("paused")}
                className="btn btn-amber flex-1"
                data-testid="pause-btn"
              >
                <Pause className="w-5 h-5"/> Pause
              </button>
              <button
                onClick={() => { setPhase("paused"); setReflectPrompt(true); }}
                className="btn btn-ghost flex-1"
                data-testid="end-practice-btn"
              >
                <Square className="w-4 h-4"/> End
              </button>
            </div>
          )}
          {phase === "paused" && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPhase("running")}
                className="btn btn-primary flex-1"
                data-testid="resume-btn"
              >
                <Play className="w-5 h-5"/> Resume
              </button>
              <button
                onClick={() => setReflectPrompt(true)}
                className="btn btn-ghost flex-1"
                data-testid="end-practice-btn"
              >
                <Square className="w-4 h-4"/> End
              </button>
            </div>
          )}
          {phase === "done" && (
            <button
              onClick={() => setReflectPrompt(true)}
              className="btn btn-primary w-full"
              data-testid="save-btn"
            >
              Save practice
            </button>
          )}
        </div>

        <p className="mt-5 text-sm text-[hsl(var(--ink-soft))] text-center max-w-xs">
          {phase === "ready"
            ? `Tap Start to begin a ${requiredMin}-minute practice.`
            : `Reach ${requiredMin} minutes to earn XP. Shorter sessions still save.`}
        </p>
      </div>

      {reflectPrompt && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center" data-testid="reflection-sheet">
          <div className="absolute inset-0 bg-[hsl(var(--ink))]/40" onClick={() => setReflectPrompt(false)} />
          <div className="relative surface w-full max-w-sm m-4 p-6 bg-white">
            <h3 className="text-2xl text-[hsl(var(--ink))] text-center">How did it feel?</h3>
            <div className="grid grid-cols-3 gap-2 mt-5">
              {[
                { k: "hard", label: "Hard", emoji: "😓" },
                { k: "good", label: "Good", emoji: "👍" },
                { k: "amazing", label: "Amazing", emoji: "🤩" },
              ].map((o) => (
                <button
                  key={o.k}
                  onClick={() => finish(o.k)}
                  className="btn btn-ghost py-5 flex-col"
                  data-testid={`reflection-${o.k}`}
                >
                  <span className="text-2xl">{o.emoji}</span>
                  <span className="text-sm">{o.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => finish(null)}
              className="mt-4 text-sm font-semibold text-[hsl(var(--ink-soft))] mx-auto block"
              data-testid="reflection-skip"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      <CelebrationModal
        open={!!celebration}
        onClose={() => { setCelebration(null); nav("/"); }}
        title={celebration?.title}
        subtitle={celebration?.subtitle}
        mascotState={celebration?.mascot}
        cta="Back home"
        testid="drum-celebration"
      />
    </div>
  );
}
